import { useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { projectsApi, usersApi, drawingsApi, type DrawingSet } from '@/lib/apiClient';
import { fmtDateTime } from '@/lib/dates';
import { Button, Modal } from '@/components';
import { GraphConsentError, uploadToJobFolder } from '@/lib/graphUpload';

// Drawings (CMS expansion): versioned drawing SETS. PMs asked to keep sets
// whole (no per-sheet split), so this is a purpose-built set manager over the
// SharePoint Job Folder's "Drawings" subfolder. The drawing_sets table is the
// index (the tab reads it, not a live SharePoint browse); each row links out
// to the PDF in SharePoint. A "new revision" supersedes the prior set.

const GRAPH_SCOPES = ['Files.ReadWrite.All'];
const SHAREPOINT_SUBFOLDER = 'Drawings';

// Primary grouping. Order here = display order of the groups.
const SET_TYPES = ['Bid', 'Permit', 'IFC', 'As-Built', 'Shop Drawings', 'Other'] as const;
const DISCIPLINES = ['Architectural', 'Structural', 'MEP', 'Civil', 'Combined'] as const;
const TYPE_HINT: Record<string, string> = { IFC: 'Issued for Construction' };

const css = `
  .dr-row, .dr-head {
    display: grid; grid-template-columns: minmax(0, 1fr) 150px 110px 168px 96px;
    gap: 12px; align-items: center; padding: 11px var(--ew-s-4);
    border-bottom: 1px solid var(--ew-n-100);
  }
  .dr-head {
    font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase; color: var(--ew-n-500); padding: 9px var(--ew-s-4);
  }
  .dr-row { cursor: pointer; }
  .dr-row:last-child { border-bottom: 0; }
  .dr-row:hover { background: var(--ew-n-50); }
  .dr-name { font: var(--ew-fw-semibold) var(--ew-fs-body-sm)/1.3 var(--ew-font); color: var(--ew-dark); overflow-wrap: anywhere; }
  .dr-file { font-size: 11.5px; color: var(--ew-n-400); overflow-wrap: anywhere; }
  .dr-chip {
    display: inline-block; border-radius: var(--ew-r-pill); padding: 1px 8px; margin-top: 3px;
    font: var(--ew-fw-medium) 10px/1.6 var(--ew-font); background: var(--ew-n-100); color: var(--ew-n-600);
  }
  .dr-vchip {
    display: inline-block; border-radius: var(--ew-r-sm); padding: 1px 7px;
    font: var(--ew-fw-semibold) 11px/1.6 var(--ew-font); font-variant-numeric: tabular-nums;
    background: var(--ew-n-100); color: var(--ew-n-600);
  }
  .dr-vbtn { border: 0; background: none; cursor: pointer; margin-left: 6px; font: var(--ew-fw-medium) 11px/1.6 var(--ew-font); color: var(--ew-quality); }
  .dr-act { border: 0; background: none; cursor: pointer; font: var(--ew-fw-medium) 12px/1 var(--ew-font); color: var(--ew-green-700); padding: 2px 4px; }
  .dr-hist { background: var(--ew-n-50); border-bottom: 1px solid var(--ew-n-100); padding: 8px var(--ew-s-4) 8px 28px; }
  .dr-hist-row { display: flex; align-items: center; gap: 10px; padding: 6px 0; font-size: 12.5px; flex-wrap: wrap; }
  .dr-group { font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption); text-transform: uppercase; color: var(--ew-green-700); padding: 14px var(--ew-s-4) 6px; }
`;

function fmtIssue(s: string | null): string {
  // Date-only column — format from the string to avoid a timezone day-shift.
  if (!s) return '';
  const [y, m, d] = s.slice(0, 10).split('-');
  return y && m && d ? `${+m}/${+d}/${y}` : '';
}
function fmtSize(bytes: number | null): string {
  if (bytes == null) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DrawingsTab({ projectId }: { projectId: number }) {
  const { instance, accounts } = useMsal();
  const qc = useQueryClient();

  const getToken = async (): Promise<string> => {
    try {
      const r = await instance.acquireTokenSilent({ scopes: GRAPH_SCOPES, account: accounts[0] });
      return r.accessToken;
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        const r = await instance.acquireTokenPopup({ scopes: GRAPH_SCOPES });
        return r.accessToken;
      }
      throw new GraphConsentError('Graph consent required');
    }
  };

  const projectQuery = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId) as Promise<{ jobNumber: string; title: string; jobFolderUrl: string | null }>,
    staleTime: 60_000,
  });
  const jobFolderUrl = projectQuery.data?.jobFolderUrl ?? null;
  const jobNumber = projectQuery.data?.jobNumber ?? '';

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60 * 1000 });
  const canEdit = (me?.roles.includes('Admin') ?? false) || (me?.permissions.includes('projects.edit') ?? false);

  const setsQuery = useQuery({
    queryKey: ['drawings', projectId],
    queryFn: () => drawingsApi.sets(projectId),
    staleTime: 30_000,
  });

  // Group rows by chain (current = max version), then bucket the current row of
  // each chain by set type. Superseded revisions live in the chain's history.
  const { groups, chainsById } = useMemo(() => {
    const byChain = new Map<number, DrawingSet[]>();
    for (const s of setsQuery.data ?? []) {
      const list = byChain.get(s.chainId) ?? [];
      list.push(s);
      byChain.set(s.chainId, list);
    }
    for (const list of byChain.values()) list.sort((a, b) => b.version - a.version);
    const current = [...byChain.values()].map((l) => l[0]);
    const buckets = new Map<string, DrawingSet[]>();
    for (const s of current) {
      const key = s.setType && (SET_TYPES as readonly string[]).includes(s.setType) ? s.setType : 'Untyped';
      const arr = buckets.get(key) ?? [];
      arr.push(s);
      buckets.set(key, arr);
    }
    for (const arr of buckets.values()) arr.sort((a, b) => a.setName.localeCompare(b.setName));
    const ordered: { type: string; rows: DrawingSet[] }[] = [];
    for (const t of SET_TYPES) if (buckets.has(t)) ordered.push({ type: t, rows: buckets.get(t)! });
    if (buckets.has('Untyped')) ordered.push({ type: 'Untyped', rows: buckets.get('Untyped')! });
    return { groups: ordered, chainsById: byChain };
  }, [setsQuery.data]);

  const [expandedChain, setExpandedChain] = useState<number | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addReviseId, setAddReviseId] = useState<number | null>(null); // preselect a set to revise
  const [editRow, setEditRow] = useState<DrawingSet | null>(null);

  const currentSets = groups.flatMap((g) => g.rows);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['drawings', projectId] });
  const open = (url: string) => window.open(url, '_blank', 'noopener,noreferrer');

  const banner = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--ew-s-3)', background: '#fff', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: '10px var(--ew-s-4)', marginBottom: 'var(--ew-s-4)', flexWrap: 'wrap' }}>
      <span style={{ font: 'var(--ew-fw-semibold) 11px/1 var(--ew-font)', letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--ew-quality)' }}>◇ Stored in the SharePoint Job Folder</span>
      <span style={{ font: 'var(--ew-fw-regular) 12px var(--ew-font)', color: 'var(--ew-n-500)' }}>
        Whole drawing sets, versioned — a new revision supersedes the prior one. SharePoint keeps the files; the Project Tracker keeps the index.
      </span>
      {canEdit && jobFolderUrl ? (
        <div style={{ marginLeft: 'auto' }}>
          <button className="btn btn-primary" onClick={() => { setAddReviseId(null); setAddOpen(true); }}>+ Add set</button>
        </div>
      ) : null}
    </div>
  );

  if (!jobFolderUrl) {
    return (
      <>
        <style>{css}</style>
        {banner}
        <div style={{ padding: 'var(--ew-s-5)', textAlign: 'center', color: 'var(--ew-n-500)', fontSize: 13, background: '#fff', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)' }}>
          This job has no linked SharePoint Job Folder yet. Create or link it on the <b>Documents</b> tab, then drawing sets can be uploaded here.
        </div>
      </>
    );
  }

  const isLoading = setsQuery.isLoading;
  const isEmpty = !isLoading && currentSets.length === 0;

  return (
    <>
      <style>{css}</style>
      {banner}

      <div className="pw-card">
        {isLoading ? (
          <div style={{ padding: 'var(--ew-s-5)', color: 'var(--ew-n-500)', fontSize: 13 }}>Loading drawing sets…</div>
        ) : isEmpty ? (
          <div style={{ padding: 'var(--ew-s-5)', textAlign: 'center', color: 'var(--ew-n-500)', fontSize: 13 }}>
            No drawing sets yet.{canEdit ? ' Use “Add set” to upload the first one.' : ''}
          </div>
        ) : (
          <>
            <div className="dr-head">
              <span>Set</span><span>Issue date</span><span>Rev</span><span>Uploaded</span><span aria-label="Actions" />
            </div>
            {groups.map((g) => (
              <div key={g.type}>
                <div className="dr-group">{g.type === 'Untyped' ? 'Untyped' : g.type}{TYPE_HINT[g.type] ? ` · ${TYPE_HINT[g.type]}` : ''}</div>
                {g.rows.map((s) => {
                  const history = (chainsById.get(s.chainId) ?? []).filter((r) => r.id !== s.id);
                  const expanded = expandedChain === s.chainId;
                  return (
                    <div key={s.id}>
                      <div className="dr-row" onClick={() => open(s.webUrl)}>
                        <div>
                          <div className="dr-name">{s.setName}</div>
                          <div className="dr-file">{s.fileName}{s.sizeBytes ? ` · ${fmtSize(s.sizeBytes)}` : ''}</div>
                          {s.discipline ? <span className="dr-chip">{s.discipline}</span> : null}
                        </div>
                        <span style={{ fontSize: 12.5, color: 'var(--ew-n-600)' }}>{fmtIssue(s.issueDate) || '—'}</span>
                        <span style={{ fontSize: 12.5, color: 'var(--ew-n-600)' }}>{s.revLabel || '—'}</span>
                        <span style={{ fontSize: 12, color: 'var(--ew-n-500)' }}>
                          {s.uploadedBy ?? '—'}<br /><span style={{ fontSize: 11, color: 'var(--ew-n-400)' }}>{fmtDateTime(s.uploadedAt)}</span>
                        </span>
                        <span onClick={(e) => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                          <span className="dr-vchip">v{s.version}</span>
                          {history.length > 0 ? (
                            <button className="dr-vbtn" onClick={() => setExpandedChain(expanded ? null : s.chainId)}>
                              {expanded ? 'Hide' : `${history.length} prior`}
                            </button>
                          ) : null}
                          {canEdit ? <button className="dr-act" onClick={() => setEditRow(s)} title="Edit tags / delete">Edit</button> : null}
                        </span>
                      </div>
                      {expanded && history.length > 0 ? (
                        <div className="dr-hist">
                          {history.map((h) => (
                            <div key={h.id} className="dr-hist-row">
                              <span className="dr-vchip">v{h.version}</span>
                              <button className="dr-act" onClick={() => open(h.webUrl)} style={{ color: 'var(--ew-quality)' }}>{h.fileName}</button>
                              <span style={{ color: 'var(--ew-n-500)' }}>{fmtIssue(h.issueDate) || h.revLabel || ''}</span>
                              <span style={{ color: 'var(--ew-n-400)', marginLeft: 'auto' }}>{h.uploadedBy ?? ''} · {fmtDateTime(h.uploadedAt)}</span>
                              {h.note ? <span style={{ flexBasis: '100%', color: 'var(--ew-n-500)', fontSize: 12 }}>{h.note}</span> : null}
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ))}
          </>
        )}
      </div>

      {addOpen ? (
        <AddSetModal
          projectId={projectId}
          jobNumber={jobNumber}
          jobFolderUrl={jobFolderUrl}
          getToken={getToken}
          currentSets={currentSets}
          reviseId={addReviseId}
          onDone={() => { setAddOpen(false); invalidate(); }}
          onClose={() => setAddOpen(false)}
        />
      ) : null}

      {editRow ? (
        <EditSetModal
          row={editRow}
          onDone={() => { setEditRow(null); invalidate(); }}
          onClose={() => setEditRow(null)}
        />
      ) : null}
    </>
  );
}

// ─── Add / revise a drawing set ─────────────────────────────────────────────
function AddSetModal({ projectId, jobNumber, jobFolderUrl, getToken, currentSets, reviseId, onDone, onClose }: {
  projectId: number;
  jobNumber: string;
  jobFolderUrl: string;
  getToken: () => Promise<string>;
  currentSets: DrawingSet[];
  reviseId: number | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [mode, setMode] = useState<'new' | 'revision'>(reviseId != null ? 'revision' : 'new');
  const [replaceId, setReplaceId] = useState<string>(reviseId != null ? String(reviseId) : '');
  const prior = currentSets.find((s) => s.id === Number(replaceId)) ?? null;

  const [setName, setSetName] = useState('');
  const [setType, setSetType] = useState('');
  const [discipline, setDiscipline] = useState('');
  const [revLabel, setRevLabel] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [note, setNote] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // In revision mode the set identity comes from the prior set; the PM only
  // supplies the new file + rev/issue/note.
  const effName = mode === 'revision' ? (prior?.setName ?? '') : setName.trim();
  const effType = mode === 'revision' ? (prior?.setType ?? null) : (setType || null);
  const effDisc = mode === 'revision' ? (prior?.discipline ?? null) : (discipline || null);

  const ext = file ? (/\.([a-z0-9]+)$/i.exec(file.name)?.[1] ?? 'pdf') : 'pdf';
  const sanitize = (s: string) => s.replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, ' ').trim();
  const builtName = file && effName
    ? `${sanitize(`${jobNumber ? jobNumber + ' - ' : ''}${effName}${revLabel ? ' - ' + revLabel : issueDate ? ' - ' + issueDate : ''}`)}.${ext}`
    : null;

  const submit = async () => {
    if (!file) { setError('Choose the set PDF first.'); return; }
    if (mode === 'revision' && !prior) { setError('Pick which set this revises.'); return; }
    if (mode === 'new' && !effName) { setError('Give the set a name.'); return; }
    if (!builtName) { setError('Missing details to build the file name.'); return; }
    setBusy(true); setError(null);
    try {
      const token = await getToken();
      const uploaded = await uploadToJobFolder({ getToken: async () => token, jobFolderUrl, subfolder: SHAREPOINT_SUBFOLDER, fileName: builtName, file });
      // The FILE is safely in SharePoint now — a registration failure must not
      // read as an upload failure.
      try {
        await drawingsApi.registerSet({
          projectId,
          driveItemId: uploaded.id,
          webUrl: uploaded.webUrl,
          fileName: uploaded.name,
          sizeBytes: uploaded.size,
          setName: effName,
          setType: effType ?? undefined,
          discipline: effDisc ?? undefined,
          revLabel: revLabel.trim() || undefined,
          issueDate: issueDate || undefined,
          note: note.trim() || undefined,
          ...(mode === 'revision' && prior ? { replaceVersionId: prior.id } : {}),
        });
      } catch (regErr: any) {
        setError(`The file uploaded to SharePoint, but recording it failed: ${regErr?.message ?? 'unknown error'}. Close and refresh — the file is safe.`);
        setBusy(false);
        return;
      }
      onDone();
    } catch (e: any) {
      setError(e?.message ?? 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const lbl: React.CSSProperties = { font: 'var(--ew-fw-medium) 11px/1 var(--ew-font)', letterSpacing: 'var(--ew-ls-caption)', textTransform: 'uppercase', color: 'var(--ew-n-500)' };
  const inp: React.CSSProperties = { border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: '7px 10px', font: 'var(--ew-fw-regular) 13px var(--ew-font)', background: '#fff' };

  return (
    <Modal isOpen onClose={onClose} title={mode === 'revision' ? 'Upload a revision' : 'Add a drawing set'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} isLoading={busy}>Upload to Job Folder</Button>
        </>
      }>
      <div style={{ fontSize: 12, color: 'var(--ew-n-500)', marginBottom: 14 }}>
        Uploads to the Job Folder's <b>Drawings</b> subfolder via Microsoft Graph.
      </div>
      {error ? <div style={{ marginBottom: 12, padding: '6px 10px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', fontSize: 12 }}>{error}</div> : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-pill)', overflow: 'hidden', alignSelf: 'flex-start' }}>
          {(['new', 'revision'] as const).map((m) => (
            <button key={m} onClick={() => setMode(m)} style={{
              border: 0, cursor: 'pointer', padding: '7px 16px', font: 'var(--ew-fw-medium) 12px/1 var(--ew-font)',
              background: mode === m ? 'var(--ew-dark)' : '#fff', color: mode === m ? '#fff' : 'var(--ew-n-600)',
            }}>{m === 'new' ? 'New set' : 'New revision of a set'}</button>
          ))}
        </div>

        {mode === 'revision' ? (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={lbl}>Which set does this revise</span>
            <select value={replaceId} onChange={(e) => setReplaceId(e.target.value)} style={inp}>
              <option value="">— pick a set —</option>
              {currentSets.map((s) => <option key={s.id} value={s.id}>{s.setName}{s.setType ? ` (${s.setType})` : ''} · v{s.version}</option>)}
            </select>
            {prior ? <span style={{ fontSize: 11.5, color: 'var(--ew-n-400)' }}>Uploads as v{prior.version + 1}; v{prior.version} is kept in revision history.</span> : null}
          </label>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 10 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={lbl}>Set name</span>
              <input value={setName} onChange={(e) => setSetName(e.target.value)} placeholder="e.g. Architectural IFC" style={inp} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={lbl}>Set type</span>
              <select value={setType} onChange={(e) => setSetType(e.target.value)} style={inp}>
                <option value="">—</option>
                {SET_TYPES.map((t) => <option key={t} value={t}>{t}{TYPE_HINT[t] ? ` (${TYPE_HINT[t]})` : ''}</option>)}
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={lbl}>Discipline (optional)</span>
              <select value={discipline} onChange={(e) => setDiscipline(e.target.value)} style={inp}>
                <option value="">—</option>
                {DISCIPLINES.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={lbl}>Revision label (optional)</span>
            <input value={revLabel} onChange={(e) => setRevLabel(e.target.value)} placeholder="e.g. Rev 2, Δ3" style={inp} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={lbl}>Issue date (optional)</span>
            <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} style={inp} />
          </label>
        </div>

        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); setFile(e.dataTransfer.files?.[0] ?? null); }}
          style={{ border: '2px dashed var(--ew-n-300)', borderRadius: 'var(--ew-r-md)', padding: '26px 16px', textAlign: 'center', cursor: 'pointer', color: 'var(--ew-n-500)', fontSize: 13, background: file ? 'var(--ew-green-50)' : '#fff' }}>
          {file ? <b style={{ color: 'var(--ew-green-900)' }}>{file.name}</b> : <>Drag the set PDF here, or <b style={{ color: 'var(--ew-green-700)' }}>browse</b></>}
          <div style={{ fontSize: 11.5, color: 'var(--ew-n-400)', marginTop: 4 }}>PDF · up to 250 MB</div>
          <input ref={inputRef} type="file" hidden accept="application/pdf,.pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </div>

        {builtName ? (
          <div style={{ padding: '8px 12px', background: 'var(--ew-n-50)', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', font: '12.5px var(--ew-font-mono, ui-monospace, monospace)', color: 'var(--ew-dark)', overflowWrap: 'anywhere' }}>
            {builtName}
          </div>
        ) : null}

        <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span style={lbl}>{mode === 'revision' ? 'What changed (optional)' : 'Note (optional)'}</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={mode === 'revision' ? 'e.g. Structural sheets reissued for RFI 042' : 'e.g. Full permit set'} style={inp} />
        </label>

        <div style={{ padding: '8px 12px', background: 'var(--ew-quality-50)', border: '1px solid var(--ew-quality-100)', borderRadius: 'var(--ew-r-md)', fontSize: 12, color: 'var(--ew-quality-700)' }}>
          SharePoint is the source of truth — the tracker stores the link + set metadata, not the file.
        </div>
      </div>
    </Modal>
  );
}

// ─── Edit tags / delete ─────────────────────────────────────────────────────
function EditSetModal({ row, onDone, onClose }: { row: DrawingSet; onClose: () => void; onDone: () => void }) {
  const [setName, setSetName] = useState(row.setName);
  const [setType, setSetType] = useState(row.setType ?? '');
  const [discipline, setDiscipline] = useState(row.discipline ?? '');
  const [revLabel, setRevLabel] = useState(row.revLabel ?? '');
  const [issueDate, setIssueDate] = useState(row.issueDate ? row.issueDate.slice(0, 10) : '');
  const [note, setNote] = useState(row.note ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  const save = async () => {
    if (!setName.trim()) { setError('Set name is required.'); return; }
    setBusy(true); setError(null);
    try {
      await drawingsApi.updateSet(row.id, {
        setName: setName.trim(),
        setType: setType || null,
        discipline: discipline || null,
        revLabel: revLabel.trim() || null,
        issueDate: issueDate || null,
        note: note.trim() || null,
      });
      onDone();
    } catch (e: any) { setError(e?.message ?? 'Save failed'); setBusy(false); }
  };
  const del = async () => {
    setBusy(true); setError(null);
    try { await drawingsApi.deleteSet(row.id); onDone(); }
    catch (e: any) { setError(e?.message ?? 'Delete failed'); setBusy(false); }
  };

  const lbl: React.CSSProperties = { font: 'var(--ew-fw-medium) 11px/1 var(--ew-font)', letterSpacing: 'var(--ew-ls-caption)', textTransform: 'uppercase', color: 'var(--ew-n-500)' };
  const inp: React.CSSProperties = { border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: '7px 10px', font: 'var(--ew-fw-regular) 13px var(--ew-font)', background: '#fff' };

  return (
    <Modal isOpen onClose={onClose} title={`Edit — ${row.fileName}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} isLoading={busy}>Save</Button>
        </>
      }>
      {error ? <div style={{ marginBottom: 12, padding: '6px 10px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', fontSize: 12 }}>{error}</div> : null}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 10 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={lbl}>Set name</span>
            <input value={setName} onChange={(e) => setSetName(e.target.value)} style={inp} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={lbl}>Set type</span>
            <select value={setType} onChange={(e) => setSetType(e.target.value)} style={inp}>
              <option value="">—</option>
              {SET_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={lbl}>Discipline</span>
            <select value={discipline} onChange={(e) => setDiscipline(e.target.value)} style={inp}>
              <option value="">—</option>
              {DISCIPLINES.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={lbl}>Revision label</span>
            <input value={revLabel} onChange={(e) => setRevLabel(e.target.value)} style={inp} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={lbl}>Issue date</span>
            <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} style={inp} />
          </label>
        </div>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span style={lbl}>Note</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} style={inp} />
        </label>

        <div style={{ borderTop: '1px solid var(--ew-n-100)', paddingTop: 12 }}>
          {confirmDel ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12.5, color: 'var(--ew-danger-700)' }}>Remove this set/revision from the index? (The PDF stays in SharePoint.)</span>
              <Button variant="secondary" size="sm" onClick={() => setConfirmDel(false)} disabled={busy}>Keep</Button>
              <button className="btn" onClick={del} disabled={busy} style={{ background: 'var(--ew-danger)', color: '#fff', border: 0, borderRadius: 'var(--ew-r-md)', padding: '6px 14px', cursor: 'pointer', fontSize: 13 }}>Remove</button>
            </div>
          ) : (
            <button className="dr-act" onClick={() => setConfirmDel(true)} style={{ color: 'var(--ew-danger-700)' }}>Remove from index…</button>
          )}
        </div>
      </div>
    </Modal>
  );
}
